import React, { useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { components } from 'react-select';
import xIcon from '../../assets/icons/x.svg';

/**
 * Custom DropdownIndicator for multiselects
 * Shows blue X (clear all) and chevron when there's a value
 */
export const CustomDropdownIndicator = ({ hasValue, onClear, isMulti, ...props }) => {
  // For multiselect with values, show both X (clear all) and chevron (dropdown toggle)
  if (isMulti && hasValue && onClear) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <IconButton
          data-multiselect-clear="true"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            onClear();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }}
          sx={{
            padding: '4px',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <img 
            src={xIcon} 
            alt="Clear" 
            style={{ 
              width: '18px', 
              height: '18px',
              filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(200deg) brightness(104%) contrast(97%)'
            }} 
          />
        </IconButton>
        <Box component="span" data-multiselect-chevron="true" sx={{ display: 'flex', alignItems: 'center' }}>
          <components.DropdownIndicator {...props} />
        </Box>
      </Box>
    );
  }

  return (
    <Box component="span" data-multiselect-chevron="true" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <components.DropdownIndicator {...props} />
    </Box>
  );
};

/**
 * ValueContainer for multiselects
 * Shows "Label: X sélectionnées" format instead of individual tags
 */
export const MultiselectValueContainer = ({ children, label, getValue, countColor = '#007BC6', ...props }) => {
  const getValueFn = getValue || props.getValue;
  const allValues = Array.isArray(getValueFn?.()) ? getValueFn() : [];
  
  // Exclude special options from count
  const actualIds = allValues.filter(v => v !== 'ALL_MOBILE' && v !== 'ALL_PHYSIQUE');
  const count = actualIds.length;
  
  if (count === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }
  
  return (
    <components.ValueContainer {...props}>
      <div style={{ 
        fontSize: '16px', 
        fontWeight: 600, 
        fontFamily: 'Montserrat, sans-serif',
        display: 'flex',
        alignItems: 'center'
      }}>
        {label && <span style={{ color: '#38485C' }}>{label}:&nbsp;</span>}
        <span style={{ color: countColor }}>{count} {count === 1 ? 'sélectionnée' : 'sélectionnées'}</span>
      </div>
    </components.ValueContainer>
  );
};

export const ScrollAwareOption = (props) => {
  const touchStartY = useRef(null);
  return (
    <components.Option
      {...props}
      innerProps={{
        ...props.innerProps,
        onTouchStart(e) {
          touchStartY.current = e.touches[0].clientY;
          props.innerProps?.onTouchStart?.(e);
        },
        onTouchEnd(e) {
          const delta = Math.abs(e.changedTouches[0].clientY - (touchStartY.current ?? e.changedTouches[0].clientY));
          if (delta > 8) {
            e.preventDefault();
            return;
          }
          props.innerProps?.onTouchEnd?.(e);
        },
      }}
    />
  );
};

export const MultiselectOption = (props) => {
  const { children, isSelected, innerProps } = props;
  const touchStartY = useRef(null);

  const mergedInnerProps =
    innerProps &&
    ({
      ...innerProps,
      onTouchStart(e) {
        touchStartY.current = e.touches[0].clientY;
        innerProps.onTouchStart?.(e);
      },
      onTouchEnd(e) {
        const delta = Math.abs(e.changedTouches[0].clientY - (touchStartY.current ?? e.changedTouches[0].clientY));
        if (delta > 8) return; // scroll — ignore
        e.preventDefault();
        innerProps.onClick?.(e);
      },
    });

  return (
    <components.Option {...props} {...(mergedInnerProps && { innerProps: mergedInnerProps })}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        {isSelected && (
          <span style={{ 
            color: '#027BC6', 
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '1',
            flexShrink: 0,
            width: '16px',
            display: 'inline-block',
            marginTop: '2px'
          }}>✓</span>
        )}
        {!isSelected && (
          <span style={{ 
            width: '16px',
            height: '16px',
            display: 'inline-block',
            flexShrink: 0
          }}></span>
        )}
        <span style={{ 
          fontWeight: isSelected ? 700 : 600,
          color: isSelected ? '#027BC6' : '#333',
          flex: 1,
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}>{children}</span>
      </div>
    </components.Option>
  );
};

export const MultiselectMenuList = (props) => {
  // Get props from both direct props (passed from CreationModal) and selectProps (for backward compatibility)
  const selectProps = props.selectProps || {};
  const mobileCaisses = props.mobileCaisses || selectProps.mobileCaisses;
  const physiqueCaisses = props.physiqueCaisses || selectProps.physiqueCaisses;
  const actualIds = props.actualIds || selectProps.actualIds;
  const onSpecialOptionClick = props.onSpecialOptionClick || selectProps.onSpecialOptionClick;
  const { innerRef } = props;
  const menuListRef = useRef(null);
  const prevActualIdsRef = useRef(actualIds);
  const wasMountedRef = useRef(false);
  
  // Get menuIsOpen from props (passed directly) or selectProps (fallback)
  // react-select passes menuIsOpen in selectProps when using controlled menuIsOpen prop
  const menuIsOpen = props.menuIsOpen !== undefined ? props.menuIsOpen : (selectProps.menuIsOpen || false);
  
  // Track previous menuIsOpen value to detect changes
  const prevMenuIsOpenRef = useRef(menuIsOpen);
  
  const combinedRef = useCallback((node) => {
    const prevNode = menuListRef.current;
    const isUnmounting = prevNode && !node;
    
    const isRemount = wasMountedRef.current && menuListRef.current && node && menuListRef.current !== node;
    
    if (innerRef) {
      innerRef(node);
    }
    
    // Save scroll position before losing the ref, but only if menu is still open and scroll wasn't already cleared
    if (isUnmounting) {
      const currentScroll = window.__multiselectScrollPosition;
      if (currentScroll === null) {
        // Scroll was already cleared by onMenuClose, don't save it
      } else if (menuListRef.current && menuIsOpen) {
        // Menu is still open, save scroll position for remount
        const scrollTop = menuListRef.current.scrollTop;
        if (scrollTop > 0) {
          window.__multiselectScrollPosition = scrollTop;
        }
      }
    } else if (menuListRef.current && !node && menuIsOpen) {
      // Saving scroll during selection (remounting)
      const scrollTop = menuListRef.current.scrollTop;
      if (scrollTop > 0) {
        window.__multiselectScrollPosition = scrollTop;
      }
    }
    
    // Clear scroll position when unmounting and menu is closed
    if (isUnmounting && !menuIsOpen) {
      window.__multiselectScrollPosition = null;
    }
    
    menuListRef.current = node;
    wasMountedRef.current = !!node;
    
    // Restore scroll position on remount
    if (node && isRemount && window.__multiselectScrollPosition !== null) {
      const savedScroll = window.__multiselectScrollPosition;
      node.scrollTop = savedScroll;
      
      setTimeout(() => {
        if (node && node === menuListRef.current) {
          node.scrollTop = savedScroll;
        }
      }, 0);
    }
  }, [innerRef, menuIsOpen]);
  
  // Clear scroll position when menu closes
  useEffect(() => {
    const prevValue = prevMenuIsOpenRef.current;
    const currentValue = menuIsOpen;
    
    if (prevValue !== currentValue) {
      if (prevValue && !currentValue) {
        window.__multiselectScrollPosition = null;
      }
      prevMenuIsOpenRef.current = currentValue;
    }
  }, [menuIsOpen]);
  
  // Find the actual scrollable element when menu opens
  useEffect(() => {
    if (!menuIsOpen) {
      return;
    }
    
    const findScrollableElement = () => {
      if (!menuListRef.current) {
        return null;
      }
      
      // Check if MenuList itself is scrollable
      const computedStyle = window.getComputedStyle(menuListRef.current);
      if (computedStyle.overflowY === 'auto' || computedStyle.overflowY === 'scroll') {
        return menuListRef.current;
      }
      
      // Look for scrollable parent (react-select's Menu component)
      let parent = menuListRef.current.parentElement;
      let depth = 0;
      while (parent && depth < 5) {
        const parentStyle = window.getComputedStyle(parent);
        if (parentStyle.overflowY === 'auto' || parentStyle.overflowY === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
        depth++;
      }
      
      // Fallback to MenuList
      return menuListRef.current;
    };
    
    const timeout = setTimeout(() => {
      const scrollableElement = findScrollableElement();
      if (scrollableElement) {
        menuListRef.current = scrollableElement;
      }
    }, 0);
    
    return () => clearTimeout(timeout);
  }, [menuIsOpen]);
  
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.__multiselectScrollPosition) {
      window.__multiselectScrollPosition = null;
    }
    
    const handleScroll = () => {
      if (menuListRef.current) {
        const scrollTop = menuListRef.current.scrollTop;
        window.__multiselectScrollPosition = scrollTop;
      }
    };
    
    if (menuIsOpen && menuListRef.current) {
      const scrollElement = menuListRef.current;
      scrollElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
        // Clear scroll position when listener is removed (menu is closing)
        window.__multiselectScrollPosition = null;
      };
    } else {
      // If menu is not open, ensure scroll is cleared
      if (!menuIsOpen && window.__multiselectScrollPosition !== null) {
        window.__multiselectScrollPosition = null;
      }
    }
  }, [menuIsOpen]);
  
  
  useLayoutEffect(() => {
    const idsChanged = JSON.stringify(prevActualIdsRef.current) !== JSON.stringify(actualIds);
    
    // Restore scroll if IDs changed (selection changed) or component remounted with saved scroll
    const shouldRestore = (idsChanged || window.__multiselectScrollPosition !== null) && 
                          menuIsOpen && 
                          menuListRef.current && 
                          window.__multiselectScrollPosition !== null;
    
    if (shouldRestore) {
      if (idsChanged) {
        prevActualIdsRef.current = actualIds;
      }
      
      const savedScroll = window.__multiselectScrollPosition;
      menuListRef.current.scrollTop = savedScroll;
      
      // Multiple timeouts to ensure scroll is restored after react-select re-renders
      const timeout1 = setTimeout(() => {
        if (menuListRef.current) {
          menuListRef.current.scrollTop = savedScroll;
        }
      }, 0);
      
      const timeout2 = setTimeout(() => {
        if (menuListRef.current) {
          menuListRef.current.scrollTop = savedScroll;
        }
      }, 10);
      
      const timeout3 = setTimeout(() => {
        if (menuListRef.current) {
          menuListRef.current.scrollTop = savedScroll;
        }
      }, 50);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
      };
    } else if (idsChanged) {
      prevActualIdsRef.current = actualIds;
    }
  }, [actualIds, menuIsOpen]);
  
  if (!mobileCaisses || !physiqueCaisses || !onSpecialOptionClick) {
    return <components.MenuList {...props} innerRef={combinedRef} />;
  }
  
  const allMobileIds = mobileCaisses.map(c => c.id);
  const allPhysiqueIds = physiqueCaisses.map(c => c.id);
  const allMobileSelected = allMobileIds.length > 0 && 
    allMobileIds.every(id => actualIds?.includes(id));
  const allPhysiqueSelected = allPhysiqueIds.length > 0 && 
    allPhysiqueIds.every(id => actualIds?.includes(id));
  
  return (
    <components.MenuList {...props} innerRef={combinedRef}>
      {mobileCaisses.length > 0 && (
        <Box
          component="div"
          data-special-option="true"
          px={2}
          py={1.5}
          display="flex"
          alignItems="center"
          sx={{
            cursor: 'pointer',
            backgroundColor: allMobileSelected ? '#f0f9ff' : 'transparent',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 10000
          }}
          onMouseEnter={(e) => {
            if (!allMobileSelected) {
              e.currentTarget.style.backgroundColor = '#f9fafb';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = allMobileSelected ? '#f0f9ff' : 'transparent';
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
            if (onSpecialOptionClick) {
              onSpecialOptionClick('ALL_MOBILE', allMobileSelected);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
          }}
        >
          <span style={{ 
            color: '#027BC6', 
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '1',
            width: '16px',
            display: 'inline-block',
            marginRight: '8px'
          }}>
            {allMobileSelected ? '✓' : ''}
          </span>
          <Typography
            style={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: allMobileSelected ? 700 : 700,
              color: allMobileSelected ? '#027BC6' : '#027BC6',
              whiteSpace: 'nowrap'
            }}
          >
            Toutes les caisses mobiles
          </Typography>
        </Box>
      )}
      
      {physiqueCaisses.length > 0 && (
        <Box
          component="div"
          data-special-option="true"
          px={2}
          py={1.5}
          display="flex"
          alignItems="center"
          sx={{
            cursor: 'pointer',
            backgroundColor: allPhysiqueSelected ? '#f0f9ff' : 'transparent',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 10000
          }}
          onMouseEnter={(e) => {
            if (!allPhysiqueSelected) {
              e.currentTarget.style.backgroundColor = '#f9fafb';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = allPhysiqueSelected ? '#f0f9ff' : 'transparent';
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
            if (onSpecialOptionClick) {
              onSpecialOptionClick('ALL_PHYSIQUE', allPhysiqueSelected);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.nativeEvent) {
              e.nativeEvent.stopImmediatePropagation();
            }
          }}
        >
          <span style={{ 
            color: '#027BC6', 
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '1',
            width: '16px',
            display: 'inline-block',
            marginRight: '8px'
          }}>
            {allPhysiqueSelected ? '✓' : ''}
          </span>
          <Typography
            style={{
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: allPhysiqueSelected ? 700 : 700,
              color: allPhysiqueSelected ? '#027BC6' : '#027BC6',
              whiteSpace: 'nowrap'
            }}
          >
            Toutes les caisses physiques
          </Typography>
        </Box>
      )}
      
      {(mobileCaisses.length > 0 || physiqueCaisses.length > 0) && (
        <Box sx={{ 
          borderTop: '1px solid #e5e7eb',
          borderBottom: '1px solid #e5e7eb',
          py: 0.5
        }} />
      )}
      
      {props.children}
    </components.MenuList>
  );
};

